import React from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import Badge from '@atlaskit/badge';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import type { storyPointEstimate_issueFieldStoryPointEstimateReadviewFull_StoryPointEstimateReadView$key as StoryPointEstimateFragment } from '@atlassian/jira-relay/src/__generated__/storyPointEstimate_issueFieldStoryPointEstimateReadviewFull_StoryPointEstimateReadView.graphql';
import { useBespokeEmptyFieldTextExperiment } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-experiment/index.tsx';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import {
	STORY_POINT_ESTIMATE_BADGE_TEST_ID,
	STORY_POINT_ESTIMATE_TEST_ID,
} from '../../common/constants.tsx';
import type { StoryPointEstimateReadViewProps } from './types.tsx';
import messages from './messages.tsx';

export const StoryPointEstimateReadView = ({
	fragmentRef,
	shouldRenderHoverPopover,
}: StoryPointEstimateReadViewProps): React.JSX.Element => {
	const { formatNumber, formatMessage } = useIntl();
	const noValueTextExperiment = formatMessage(messages.noValueTextExperiment);
	const { isExperimentEnabled } = useBespokeEmptyFieldTextExperiment();

	const data = useFragment<StoryPointEstimateFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment storyPointEstimate_issueFieldStoryPointEstimateReadviewFull_StoryPointEstimateReadView on JiraNumberField {
				id
				number
			}
		`,
		fragmentRef,
	);

	const { number } = data;

	const hoverPopoverRefFactory = shouldRenderHoverPopover
		? // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
			useHoverPopoverContentRef(data.id)
		: null;

	if (number === null || number === undefined) {
		return (
			<span data-testid={STORY_POINT_ESTIMATE_TEST_ID}>
				<EmptyFieldText emptyText={isExperimentEnabled ? noValueTextExperiment : undefined} />
			</span>
		);
	}

	let hoverPopoverRef: HoverPopoverContentRef = null;
	if (hoverPopoverRefFactory) {
		hoverPopoverRef = hoverPopoverRefFactory({
			content: formatNumber(number),
		});
	}

	if (shouldRenderHoverPopover) {
		return (
			<span data-testid={STORY_POINT_ESTIMATE_TEST_ID}>
				<BadgeWrapper>
					<Badge testId={STORY_POINT_ESTIMATE_BADGE_TEST_ID}>
						<Text maxLines={1} ref={hoverPopoverRef}>
							{formatNumber(number)}
						</Text>
					</Badge>
				</BadgeWrapper>
			</span>
		);
	}

	return (
		<span data-testid={STORY_POINT_ESTIMATE_TEST_ID}>
			<BadgeWrapper>
				<Badge testId={STORY_POINT_ESTIMATE_BADGE_TEST_ID}>{formatNumber(number)}</Badge>
			</BadgeWrapper>
		</span>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const BadgeWrapper = styled.div({
	borderWidth: token('border.width.selected'),
	borderStyle: 'solid',
	borderColor: 'transparent',
});
